import { useRef } from 'react';
import ReCaptcha from 'react-google-recaptcha';
import { googleCaptchaSiteKey } from '@gonfalon/constants/constants';
import { isCaptchaEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';

interface UseInvisibleCaptchaOptions {
  /** When false, nothing renders and `getToken` resolves to `undefined`. */
  enabled?: boolean;
}

/**
 * Runs an invisible reCAPTCHA challenge on demand.
 *
 * `getToken` resolves once the challenge finishes, so callers can `await` a
 * token inline instead of threading the result back through a callback.
 *
 * @example
 * ```tsx
 * const { getToken, captcha } = useInvisibleCaptcha();
 *
 * const onSubmit = async () => {
 *   const token = await getToken();
 *   post({ ...values, captcha: token });
 * };
 *
 * return <form onSubmit={onSubmit}>{fields}{captcha}</form>;
 * ```
 */
export function useInvisibleCaptcha({ enabled = true }: UseInvisibleCaptchaOptions = {}) {
  // reCAPTCHA is a class component with no declarative way to start a
  // challenge — `executeAsync` is the library's API — so the ref is required,
  // not a choice.
  const widgetRef = useRef<ReCaptcha>(null);
  const isEnabled = enabled && isCaptchaEnabled();

  const getToken = async (): Promise<string | undefined> => {
    if (!isEnabled) {
      return undefined;
    }

    // An unmounted widget, an expired challenge, and a widget error all resolve
    // to an empty token rather than nothing. The endpoint then rejects the
    // submission, which the caller surfaces as an ordinary submit error —
    // better than a submit button that silently does nothing.
    if (!widgetRef.current) {
      return '';
    }

    try {
      widgetRef.current.reset();

      return (await widgetRef.current.executeAsync()) ?? '';
    } catch {
      return '';
    }
  };

  return {
    isEnabled,
    getToken,
    /**
     * The widget. Render it inside the form — it has to stay mounted until
     * `getToken` resolves, or the challenge is torn down mid-flight.
     */
    captcha: isEnabled ? <ReCaptcha ref={widgetRef} sitekey={googleCaptchaSiteKey()} size="invisible" /> : null,
  };
}
